// ピクセル表面: 256px の不変タイルによるスパースな無限グリッド。
//
// - 空タイル (未登録) = 透明。カンバス外のピクセルも保持できる。
// - 書き換えは常にタイル単位の置換。旧タイルを TilePatch として返すので、
//   履歴はそれを参照するだけで undo できる。
// - 座標は「表面座標」。ラスターレイヤーでは文書座標 = 表面座標 + layer.offset。
// - この Map は Vue のリアクティブにも Pinia にも入れない (pixelStore.ts 参照)。

import type { Rect } from '@/types/geometry'
import { TILE_SIZE, type Channels, type Tile, tileKey, parseTileKey, createTile, tileIsEmpty } from './tile'

export interface TilePatch {
  pixelId: string
  key: string
  before: Tile | null
  after: Tile | null
}

export class PixelSurface {
  readonly id: string
  readonly channels: Channels
  private tiles = new Map<string, Tile>()
  /** タイルが置き換わるたびに +1。GPU キャッシュの無効化判定に使う */
  revision = 0
  /** 直近の変更タイル (GPU の部分更新用)。revision ごとのキー一覧 */
  private log: { rev: number; keys: string[] }[] = []
  private static LOG_LIMIT = 128
  private cachedBounds: Rect | null | undefined = undefined
  private cachedContentBounds: Rect | null | undefined = undefined

  constructor(id: string, channels: Channels) {
    this.id = id
    this.channels = channels
  }

  get tileCount(): number {
    return this.tiles.size
  }

  getTile(key: string): Tile | undefined {
    return this.tiles.get(key)
  }

  keys(): IterableIterator<string> {
    return this.tiles.keys()
  }

  entries(): IterableIterator<[string, Tile]> {
    return this.tiles.entries()
  }

  /** 登録タイルを覆う矩形 (タイル境界に揃う)。空なら null */
  bounds(): Rect | null {
    if (this.cachedBounds !== undefined) return this.cachedBounds
    if (this.tiles.size === 0) return (this.cachedBounds = null)
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity
    for (const key of this.tiles.keys()) {
      const { tx, ty } = parseTileKey(key)
      if (tx < x0) x0 = tx
      if (ty < y0) y0 = ty
      if (tx + 1 > x1) x1 = tx + 1
      if (ty + 1 > y1) y1 = ty + 1
    }
    return (this.cachedBounds = {
      x: x0 * TILE_SIZE,
      y: y0 * TILE_SIZE,
      w: (x1 - x0) * TILE_SIZE,
      h: (y1 - y0) * TILE_SIZE
    })
  }

  /** 不透明ピクセルを覆う正確な矩形 (初回は走査するので重い。revision ごとにキャッシュ)。空なら null */
  contentBounds(): Rect | null {
    if (this.cachedContentBounds !== undefined) return this.cachedContentBounds
    return (this.cachedContentBounds = this.scanContentBounds())
  }

  private scanContentBounds(): Rect | null {
    const c = this.channels
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity
    for (const [key, tile] of this.tiles) {
      const { tx, ty } = parseTileKey(key)
      const d = tile.data
      for (let y = 0; y < TILE_SIZE; y++) {
        for (let x = 0; x < TILE_SIZE; x++) {
          const a = d[(y * TILE_SIZE + x) * c + (c - 1)]!
          if (a === 0) continue
          const gx = tx * TILE_SIZE + x
          const gy = ty * TILE_SIZE + y
          if (gx < x0) x0 = gx
          if (gy < y0) y0 = gy
          if (gx + 1 > x1) x1 = gx + 1
          if (gy + 1 > y1) y1 = gy + 1
        }
      }
    }
    if (x0 === Infinity) return null
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 }
  }

  /**
   * タイルを置換する。null は削除。戻り値は旧タイルを含むパッチ (履歴用)。
   * 空タイル (全 0) は登録せず削除扱いにする。
   */
  writeTiles(patches: Iterable<[string, Tile | null]>): TilePatch[] {
    const out: TilePatch[] = []
    for (const [key, next] of patches) {
      const prev = this.tiles.get(key) ?? null
      const after = next && !tileIsEmpty(next) ? next : null
      if (prev === after) continue
      if (after) {
        if (after.channels !== this.channels) throw new Error('チャンネル数が一致しない')
        this.tiles.set(key, after)
      } else {
        this.tiles.delete(key)
      }
      out.push({ pixelId: this.id, key, before: prev, after })
    }
    if (out.length) {
      this.revision++
      this.cachedBounds = undefined
      this.cachedContentBounds = undefined
      this.log.push({ rev: this.revision, keys: out.map(p => p.key) })
      if (this.log.length > PixelSurface.LOG_LIMIT) this.log.splice(0, this.log.length - PixelSurface.LOG_LIMIT)
    }
    return out
  }

  /**
   * revision `since` 以降に変わったタイルのキー集合。ログが足りない (古すぎる) 場合は null。
   */
  changesSince(since: number): Set<string> | null {
    if (since === this.revision) return new Set()
    if (since > this.revision) return null
    const out = new Set<string>()
    let covered = since
    for (const e of this.log) {
      if (e.rev <= since) continue
      if (e.rev !== covered + 1) return null
      covered = e.rev
      for (const k of e.keys) out.add(k)
    }
    return covered === this.revision ? out : null
  }

  /**
   * 矩形領域を書き込む。data は rect.w * rect.h * channels の密な配列。
   * 端のタイルは既存タイルを複製してから部分更新する (COW)。
   */
  writeRect(rect: Rect, data: Uint8ClampedArray): TilePatch[] {
    const c = this.channels
    if (data.length !== rect.w * rect.h * c) {
      throw new Error(`writeRect: データ長 ${data.length} が矩形 ${rect.w}x${rect.h}x${c} と一致しない`)
    }
    if (rect.w <= 0 || rect.h <= 0) return []
    const patches: [string, Tile | null][] = []
    const tx0 = Math.floor(rect.x / TILE_SIZE)
    const ty0 = Math.floor(rect.y / TILE_SIZE)
    const tx1 = Math.floor((rect.x + rect.w - 1) / TILE_SIZE)
    const ty1 = Math.floor((rect.y + rect.h - 1) / TILE_SIZE)
    for (let ty = ty0; ty <= ty1; ty++) {
      for (let tx = tx0; tx <= tx1; tx++) {
        const key = tileKey(tx, ty)
        const prev = this.tiles.get(key)
        const next = createTile(c, prev ? new Uint8ClampedArray(prev.data) : undefined)
        const ox = tx * TILE_SIZE
        const oy = ty * TILE_SIZE
        const x0 = Math.max(rect.x, ox)
        const y0 = Math.max(rect.y, oy)
        const x1 = Math.min(rect.x + rect.w, ox + TILE_SIZE)
        const y1 = Math.min(rect.y + rect.h, oy + TILE_SIZE)
        const rowBytes = (x1 - x0) * c
        for (let y = y0; y < y1; y++) {
          const src = ((y - rect.y) * rect.w + (x0 - rect.x)) * c
          const dst = ((y - oy) * TILE_SIZE + (x0 - ox)) * c
          next.data.set(data.subarray(src, src + rowBytes), dst)
        }
        patches.push([key, next])
      }
    }
    return this.writeTiles(patches)
  }

  /** 矩形領域を密な配列として読み出す。未登録タイルの部分は 0 */
  readRect(rect: Rect): Uint8ClampedArray {
    const c = this.channels
    const out = new Uint8ClampedArray(Math.max(0, rect.w * rect.h * c))
    if (rect.w <= 0 || rect.h <= 0) return out
    const tx0 = Math.floor(rect.x / TILE_SIZE)
    const ty0 = Math.floor(rect.y / TILE_SIZE)
    const tx1 = Math.floor((rect.x + rect.w - 1) / TILE_SIZE)
    const ty1 = Math.floor((rect.y + rect.h - 1) / TILE_SIZE)
    for (let ty = ty0; ty <= ty1; ty++) {
      for (let tx = tx0; tx <= tx1; tx++) {
        const tile = this.tiles.get(tileKey(tx, ty))
        if (!tile) continue
        const ox = tx * TILE_SIZE
        const oy = ty * TILE_SIZE
        const x0 = Math.max(rect.x, ox)
        const y0 = Math.max(rect.y, oy)
        const x1 = Math.min(rect.x + rect.w, ox + TILE_SIZE)
        const y1 = Math.min(rect.y + rect.h, oy + TILE_SIZE)
        const rowBytes = (x1 - x0) * c
        for (let y = y0; y < y1; y++) {
          const src = ((y - oy) * TILE_SIZE + (x0 - ox)) * c
          const dst = ((y - rect.y) * rect.w + (x0 - rect.x)) * c
          out.set(tile.data.subarray(src, src + rowBytes), dst)
        }
      }
    }
    return out
  }

  /** タイル参照を共有した複製 (O(タイル数))。スナップショットやチャンネル保存用 */
  fork(newId: string): PixelSurface {
    const s = new PixelSurface(newId, this.channels)
    for (const [k, t] of this.tiles) s.tiles.set(k, t)
    s.cachedBounds = this.cachedBounds
    s.cachedContentBounds = this.cachedContentBounds
    return s
  }

  /** 概算バイト数 (タイル実体のみ。共有タイルは重複計上する) */
  byteLength(): number {
    return this.tiles.size * TILE_SIZE * TILE_SIZE * this.channels
  }
}
